import React from 'react';
import { Link } from 'react-router-dom';
import { PlusCircle, Layers, CheckCircle2, Clock, AlertCircle, TrendingUp, Cpu, ArrowRight } from 'lucide-react';
import { useStore } from '../store';
import { MACHINES } from '../engine/rules';

const STATUS_STYLE: Record<string, string> = {
  DRAFT:'badge-gray', REVIEW:'badge-blue', PLANNING:'badge-amber',
  APPROVED:'badge-green', COMPLETE:'badge-green',
};

export default function Dashboard() {
  const jobs = useStore(s => s.jobs);
  const counts = {
    total: jobs.length,
    inProgress: jobs.filter(j => ['REVIEW','PLANNING'].includes(j.status)).length,
    approved: jobs.filter(j => ['APPROVED','COMPLETE'].includes(j.status)).length,
    draft: jobs.filter(j => j.status === 'DRAFT').length,
  };
  const allMachines = Object.values(MACHINES);
  const boltFormers = allMachines.filter((m:any) => m.type === 'BOLT');
  const nutFormers  = allMachines.filter((m:any) => m.type === 'NUT');

  return (
    <div className="fade-in">
      {/* Page header */}
      <div className="flex-between" style={{ marginBottom: 28 }}>
        <div>
          <h1>Process Planning</h1>
          <p style={{ marginTop: 4, fontSize: 13 }}>Singhania International Ltd · Sturdfix, Ludhiana</p>
        </div>
        <Link to="/new-job" className="btn btn-navy btn-lg">
          <PlusCircle size={15} /> New Job
        </Link>
      </div>

      {/* KPI row */}
      <div className="grid-4" style={{ marginBottom: 24 }}>
        {[
          { label:'Total Jobs',    value: counts.total,      Icon: Layers,        color:'var(--c-navy)' },
          { label:'In Progress',   value: counts.inProgress, Icon: Clock,         color:'var(--c-amber)' },
          { label:'Approved',      value: counts.approved,   Icon: CheckCircle2,  color:'var(--c-green)' },
          { label:'Drafts',        value: counts.draft,      Icon: AlertCircle,   color:'var(--t-faint)' },
        ].map(({ label, value, Icon, color }) => (
          <div key={label} className="card card-pad" style={{ borderTop: `3px solid ${color}` }}>
            <div className="flex-between">
              <div>
                <div style={{ fontSize: 30, fontWeight: 700, color, letterSpacing: '-1px', lineHeight: 1 }}>{value}</div>
                <div style={{ fontSize: 12, color: 'var(--t-muted)', marginTop: 4, fontWeight: 500 }}>{label}</div>
              </div>
              <Icon size={20} color={color} strokeWidth={1.5} />
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 320px', gap: 20 }}>
        {/* Recent Jobs */}
        <div className="card">
          <div className="card-header">
            <h3>Recent Jobs</h3>
            <Link to="/jobs" className="btn btn-ghost btn-sm" style={{ gap: 4 }}>
              View all <ArrowRight size={12} />
            </Link>
          </div>
          {jobs.length === 0 ? (
            <div className="empty-state" style={{ padding: '40px 20px' }}>
              <Layers size={28} strokeWidth={1.5} style={{ margin: '0 auto 10px', display: 'block' }} />
              <p>No jobs yet — create your first process plan</p>
              <Link to="/new-job" className="btn btn-navy btn-sm" style={{ marginTop: 12 }}>
                <PlusCircle size={12} /> New Job
              </Link>
            </div>
          ) : (
            <table>
              <thead><tr>
                <th>Job ID</th><th>Part No.</th><th>Customer</th>
                <th>Template</th><th>Machine</th><th>Status</th>
              </tr></thead>
              <tbody>
                {jobs.slice(0, 8).map(j => (
                  <tr key={j.id}>
                    <td><Link to={`/jobs/${j.id}`} style={{ color:'var(--c-blue)', textDecoration:'none', fontFamily:'var(--mono)', fontSize:12 }}>{j.id.replace('JOB-','')}</Link></td>
                    <td style={{ fontFamily:'var(--mono)', fontSize:12, fontWeight:500, color:'var(--t-primary)' }}>{j.part_no || '—'}</td>
                    <td style={{ color:'var(--t-secondary)' }}>{j.customer || '—'}</td>
                    <td>{j.template ? <span className="badge badge-navy">{j.template}</span> : '—'}</td>
                    <td style={{ fontSize:12, color:'var(--t-muted)' }}>{j.selected_machine?.replace(/_/g,' ') || '—'}</td>
                    <td><span className={`badge ${STATUS_STYLE[j.status] ?? 'badge-gray'}`}>{j.status}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Machine fleet sidebar */}
        <div style={{ display:'flex', flexDirection:'column', gap:16 }}>
          <div className="card">
            <div className="card-header"><h3>Bolt Formers</h3><span className="badge badge-navy">{boltFormers.length} machines</span></div>
            <div style={{ padding:'4px 0' }}>
              {boltFormers.map((m:any) => (
                <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 20px', borderBottom:'1px solid var(--c-border)' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--t-primary)' }}>{m.name}</div>
                    <div style={{ fontSize:11, color:'var(--t-muted)' }}>Ø{m.wire_min}–{m.wire_max}mm</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--c-navy)' }}>{(m.force_kg/1000).toFixed(0)}t</div>
                    <div style={{ fontSize:10, color:'var(--t-faint)' }}>×{m.qty}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="card">
            <div className="card-header"><h3>Nut Formers</h3><span className="badge badge-amber">{nutFormers.length} machines</span></div>
            <div style={{ padding:'4px 0' }}>
              {nutFormers.map((m:any) => (
                <div key={m.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 20px', borderBottom:'1px solid var(--c-border)' }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:500, color:'var(--t-primary)' }}>{m.name}</div>
                    <div style={{ fontSize:11, color:'var(--t-muted)' }}>{m.nut_size}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontSize:12, fontWeight:600, color:'var(--c-navy)' }}>{(m.force_kg/1000).toFixed(0)}t</div>
                    <div style={{ fontSize:10, color:'var(--t-faint)' }}>×{m.qty}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
